import { createContext, useContext, type ComponentPropsWithRef, type ReactElement } from 'react';
import { cn } from '../util/classes.ts';

export type MenuProps = ComponentPropsWithRef<'ul'>;

/** Thin column wrapper for `MenuItem`s — no gap, no chrome; full-bleed rows. */
export function Menu({ className, ...props }: MenuProps): ReactElement {
    return <ul role="menu" className={cn('flex w-full flex-col p-cozy', className)} {...props} />;
}

export type MenuGroupProps = ComponentPropsWithRef<'li'> & {
    label: string;
};

export function MenuGroup({ label, className, children, ...props }: MenuGroupProps): ReactElement {
    return (
        <li role="presentation" className={cn('flex flex-col gap-[inherit]', className)} {...props}>
            <span className="px-cozy pt-cozy pb-tight text-ui-sm font-medium tracking-[0.06em] text-content-weaker">
                {label}
            </span>
            <ul role="group" aria-label={label} className="flex flex-col gap-[inherit]">
                {children}
            </ul>
        </li>
    );
}

export type MenuDividerProps = ComponentPropsWithRef<'li'>;

/** A horizontal rule between rows/sections. */
export function MenuDivider({ className, ...props }: MenuDividerProps): ReactElement {
    return <li role="separator" className={cn('my-tight h-px bg-line-weak', className)} {...props} />;
}

const MenuItemLabelContext = createContext('');

export type MenuItemProps = ComponentPropsWithRef<'button'> & {
    label: string;
};

/**
 * One row. The row itself is a `<button>` inside a presentational `<li>`, not a
 * clickable `<li>`: a sheet's swipe-to-dismiss claims the pointer on anything
 * that isn't a native control, which swallows the click, and a bare `<li>` has
 * no keyboard activation of its own.
 */
export function MenuItem({ className, label, ...props }: MenuItemProps): ReactElement {
    return (
        <MenuItemLabelContext.Provider value={label}>
            <li role="presentation">
                <button
                    type="button"
                    role="menuitem"
                    aria-label={label}
                    className={cn(
                        'flex w-full flex-row items-center gap-cozy',
                        'min-h-9 px-cozy py-tight',
                        'text-left text-ui-base text-content',
                        'rounded-md hover:bg-content/(--opacity-wash) cursor-pointer select-none',
                        'active:translate-px transition duration-state ease-state',
                        'state-disabled:pointer-events-none state-disabled:opacity-disabled',
                        className,
                    )}
                    {...props}
                />
            </li>
        </MenuItemLabelContext.Provider>
    );
}

export type MenuItemLeadingProps = ComponentPropsWithRef<'span'>;

export function MenuItemLeading({ className, ...props }: MenuItemLeadingProps): ReactElement {
    return (
        <span
            className={cn('inline-flex shrink-0 items-center text-ui-lg text-content-weaker', className)}
            {...props}
        />
    );
}

export type MenuItemLabelProps = ComponentPropsWithRef<'span'>;

export function MenuItemLabel({ className, children, ...props }: MenuItemLabelProps): ReactElement {
    const label = useContext(MenuItemLabelContext);

    return (
        <span className={cn('flex min-w-0 flex-1 flex-col gap-tight', className)} {...props}>
            {children ?? <span className="truncate">{label}</span>}
        </span>
    );
}

export type MenuItemTrailingProps = ComponentPropsWithRef<'span'>;

export function MenuItemTrailing({ className, ...props }: MenuItemTrailingProps): ReactElement {
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center gap-cozy text-ui-lg text-content-weaker',
                className,
            )}
            {...props}
        />
    );
}
